/* eslint-disable react/button-has-type */
import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import ButtonContainer from '../ButtonContainer'

import { STATUS_DOT } from './StatusDot'

const STYLE_TYPES = ['primary', 'secondary', 'tertiary', 'fourth', 'upgrade', 'danger']

const SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
}

const getButtonBackgroundColor = (theme, styleType) =>
  theme.colors.background.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']

const Button = (props) => <ButtonContainer {...props} />

const StyledButton = styled(Button)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: ${({ noMinWidth }) => (noMinWidth ? 'none;' : '70px;')}
  height: ${({ size }) => (size === SIZE.SMALL ? '32px' : size === SIZE.LARGE ? '40px' : '36px')};
  position: relative;
  padding: 8px 12px 8px 12px;
  font-size: ${({ theme, size }) =>
    size === SIZE.SMALL ? theme.typography.size.caption : theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  text-align: center;
  outline: none;
  user-select: none;
  ${(props) => (props.disabled || props.loader ? '' : 'cursor: pointer;')}
  white-space: nowrap;
  color: white;
  border: none;

  ${({ iconSameColor }) =>
    iconSameColor &&
    `
    svg,
    svg > path {
      fill: currentColor;
    }
  `}

  .is-button-internal-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .left-icon {
    margin: 0 10px 0 0;
  }

  .right-icon {
    margin: 0 0 0 10px;
  }


   .left-icon,
   .right-icon {
    svg,
    svg > path {
      fill: ${({ iconStyle }) => iconStyle?.fill || 'currentColor'};
     }
   }

  .button-counter {
    color: white;
    font-size: 10px;
    min-width: 15px;
    padding: 0 4.5px;
    border-radius: 100px;
    position: absolute;
    top: -4px;
    right: -4px;
    background-color: ${(props) =>
      props.secondary ? props.theme.secondaryColor : props.theme.primaryColor};
    line-height: 15px;
  }

  ${(props) =>
    !props.disabled &&
    `
    &:hover {
      background-color: ${props.hoverColor || props.theme.colors.ds_blue_2};
    }
  `}

  // background
  background-color: ${({ styleType = 'primary', theme, noBackground }) =>
    noBackground ? 'transparent' : getButtonBackgroundColor(theme, styleType)?.default};

  &:hover {
    background-color: ${({ styleType = 'primary', theme, disabled, noBackground }) =>
      disabled && noBackground ? 'transparent' : getButtonBackgroundColor(theme, styleType)?.hover};
  }

  &:active,
  &.active {
    background-color: ${({ styleType = 'primary', theme, disabled, noBackground }) =>
      disabled && noBackground
        ? 'transparent'
        : getButtonBackgroundColor(theme, styleType)?.active};
  }

  &:disabled {
    background-color: ${({ styleType = 'primary', theme, noBackground }) =>
      noBackground ? 'transparent' : getButtonBackgroundColor(theme, styleType)?.disabled};
  }

  &.loader {
    background-color: ${({ styleType = 'primary', theme }) =>
      getButtonBackgroundColor(theme, styleType)?.loader};
  }

  // borders
  border-width: 1px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.squareButtonRadius};
  border-color: ${({ styleType = 'primary', theme }) =>
    theme.colors.border.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.default};

  &:hover {
    border-color: ${({ styleType = 'primary', theme }) =>
      theme.colors.border.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.hover};
  }

  &:active,
  &.active {
    border-color: ${({ styleType = 'primary', theme }) =>
      theme.colors.border.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.active};
  }

  &:disabled {
    border-color: ${({ styleType = 'primary', theme }) =>
      theme.colors.border.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']
        ?.disabled};
  }

  &.loader {
    border-color: ${({ styleType = 'primary', theme }) =>
      theme.colors.border.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.loader};
  }

  // color
  color: ${({ styleType = 'primary', theme }) =>
    theme.colors.text.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.default};

  &:hover {
    color: ${({ styleType = 'primary', theme }) =>
      theme.colors.text.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.hover};
  }

  &:active,
  &.active {
    color: ${({ styleType = 'primary', theme }) =>
      theme.colors.text.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.active};
  }

  &:disabled {
    color: ${({ styleType = 'primary', theme }) =>
      theme.colors.text.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.disabled};
  }

  &.loader {
    color: ${({ styleType = 'primary', theme }) =>
      theme.colors.text.button[STYLE_TYPES.includes(styleType) ? styleType : 'primary']?.loader};
  }

  // loader
  .button-loader-wrapper {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: ${({ theme }) => theme.squareButtonRadius};
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ styleType, theme }) =>
      styleType === 'secondary' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}

    ${({ theme, styleType }) =>
      styleType === 'secondary' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}

    ${({ theme, styleType }) =>
      styleType === 'tertiary' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}

    ${({ theme, styleType }) =>
      styleType === 'fourth' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}

    ${({ theme, styleType }) =>
      styleType === 'upgrade' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}

    ${({ theme, styleType }) =>
      styleType === 'danger' &&
      `
      svg,
      svg > path {
        fill: ${theme.primary};
      }
    `}
  }

  ${({ styleType, disabled }) =>
    styleType === 'upgrade' &&
    `

    &.disabled {
      opacity: ${disabled ? 0.5 : 1};
    }
  `}
`

StyledButton.STYLE_TYPES = STYLE_TYPES
StyledButton.SIZE = SIZE

StyledButton.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.string,
  text: PropTypes.string,
  /**
   * The type attribute specifies the type of button (button|submit|reset)
   */
  type: PropTypes.string,
  /**
   * Custom inline style
   */
  style: PropTypes.object,
  /**
   * Requires 'text' and 'position' fields to be specified
   */
  popup: PropTypes.exact({
    text: PropTypes.string,
    position: PropTypes.string,
  }),
  /**
   * Requires 'text' field to be specified
   */
  confirmationModal: PropTypes.exact({
    text: PropTypes.string,
  }),
  // goBack: PropTypes.bool,
  // arrowRight: PropTypes.bool,
  // loader: PropTypes.bool,
  /**
   * Icon positioned on the left. Icon component
   */
  leftIcon: PropTypes.string,
  /**
   * Icon positioned on the right. Icon component
   */
  rightIcon: PropTypes.string,
  /**
   * Custom icon style
   */
  iconStyle: PropTypes.object,
  noBackground: PropTypes.bool,
  disabled: PropTypes.bool,
  styleType: PropTypes.oneOf(STYLE_TYPES),
  statusDotProps: PropTypes.exact({
    size: PropTypes.oneOf(['small', 'default']),
    status: PropTypes.oneOf(Object.values(STATUS_DOT)),
  }),
  hoverColor: PropTypes.string,
  counter: PropTypes.number,
  split: PropTypes.bool,
}

StyledButton.defaultProps = {
  icon: undefined,
  text: undefined,
  type: 'button',
  style: undefined,
  popup: undefined,
  confirmationModal: undefined,
  // goBack: undefined,
  // arrowRight: undefined,
  // loader: undefined,
  leftIcon: undefined,
  rightIcon: undefined,
  iconStyle: undefined,
  noBackground: false,
  disabled: false,
  styleType: undefined,
  hoverColor: undefined,
  counter: undefined,
}

export default StyledButton
